import client from 'client'
import type {
  GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse,
  GetAssetInstructionsAssetAssetIdInstructionsGetPathParams,
  GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
  GetAssetInstructionsAssetAssetIdInstructionsGet422,
} from '../../types/GetAssetInstructionsAssetAssetIdInstructionsGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetInstructionsAssetAssetIdInstructionsGetQueryKey = (
  asset_id: GetAssetInstructionsAssetAssetIdInstructionsGetPathParams['asset_id'],
  params?: GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
) => [{ url: '/asset/:asset_id/instructions', params: { asset_id: asset_id } }, ...(params ? [params] : [])] as const

export type GetAssetInstructionsAssetAssetIdInstructionsGetQueryKey = ReturnType<typeof getAssetInstructionsAssetAssetIdInstructionsGetQueryKey>

/**
 * @description Get paginated asset instructions for an asset.Retrieves a list of asset instructions that have been sentto the specified gateway asset. Results are ordered by created_atdescending (newest first) and support cursor-based pagination.Args:    asset_id: UUID of the asset to retrieve instructions for.    response: FastAPI Response object for setting headers.    cursor: Pagination cursor with after, before, max_items params.    handle: Authorized message handler dependency.    company_id: Company ID from request context (validated by AuthorisedMessageHandler).Returns:    List of AssetInstructionOut objects with X-Cursor header    containing pagination information for the next page.
 * @summary Get Asset Instructions
 * {@link /asset/:asset_id/instructions}
 */
export async function getAssetInstructionsAssetAssetIdInstructionsGet(
  asset_id: GetAssetInstructionsAssetAssetIdInstructionsGetPathParams['asset_id'],
  params?: GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse,
    ResponseErrorConfig<GetAssetInstructionsAssetAssetIdInstructionsGet422>,
    unknown
  >({ method: 'GET', url: `/asset/${asset_id}/instructions`, params, ...requestConfig })
  return res
}

export function getAssetInstructionsAssetAssetIdInstructionsGetQueryOptions(
  asset_id: GetAssetInstructionsAssetAssetIdInstructionsGetPathParams['asset_id'],
  params?: GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getAssetInstructionsAssetAssetIdInstructionsGetQueryKey(asset_id, params)
  return queryOptions<
    ResponseConfig<GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse>,
    ResponseErrorConfig<GetAssetInstructionsAssetAssetIdInstructionsGet422>,
    ResponseConfig<GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse>,
    typeof queryKey
  >({
    enabled: !!asset_id,
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetInstructionsAssetAssetIdInstructionsGet(asset_id, params, config)
    },
  })
}

/**
 * @description Get paginated asset instructions for an asset.Retrieves a list of asset instructions that have been sentto the specified gateway asset. Results are ordered by created_atdescending (newest first) and support cursor-based pagination.Args:    asset_id: UUID of the asset to retrieve instructions for.    response: FastAPI Response object for setting headers.    cursor: Pagination cursor with after, before, max_items params.    handle: Authorized message handler dependency.    company_id: Company ID from request context (validated by AuthorisedMessageHandler).Returns:    List of AssetInstructionOut objects with X-Cursor header    containing pagination information for the next page.
 * @summary Get Asset Instructions
 * {@link /asset/:asset_id/instructions}
 */
export function useGetAssetInstructionsAssetAssetIdInstructionsGet<
  TData = ResponseConfig<GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetInstructionsAssetAssetIdInstructionsGetQueryKey,
>(
  asset_id: GetAssetInstructionsAssetAssetIdInstructionsGetPathParams['asset_id'],
  params?: GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetAssetInstructionsAssetAssetIdInstructionsGetQueryResponse>,
        ResponseErrorConfig<GetAssetInstructionsAssetAssetIdInstructionsGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetInstructionsAssetAssetIdInstructionsGetQueryKey(asset_id, params)

  const query = useQuery({
    ...(getAssetInstructionsAssetAssetIdInstructionsGetQueryOptions(asset_id, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetInstructionsAssetAssetIdInstructionsGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}